import { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, TrendingUp, Share2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getBracketLeaderboard } from "@/lib/bracket.functions";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/audit";
import { buildCanonicalShareUrl } from "@/lib/share/canonicalShare";
import { bumpInsight } from "@/lib/session";
import { awardPoints } from "@/lib/gamification";
import { toast } from "sonner";
import { useLanguage } from "@/hooks/useLanguage";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  PREDICTION_IDLE_MS,
  PREDICTION_INTERACTED_MS,
  PREDICTION_BADGE_HEIGHT_PX,
} from "@/lib/pr29/predictionAutoDismiss";
import {
  canDisplay as predictionCanDisplay,
  registerAnswer as predictionRegisterAnswer,
  postVoteDismissMs,
  resolveOutcome,
} from "@/lib/pr35b/pollLifecycleBridge";
import {
  appendPredictionHistory,
  getPredictionHistory,
  type PredictionHistoryEntry,
} from "@/lib/pr35a/pollLifecycle";
import { useClaimAttention, useAttentionYield } from "@/hooks/useAttentionFocus";
import { openShareSheet } from "@/components/viral/ShareSheetHost";
import { VictoryCardCta } from "@/components/viral/VictoryCardCta";
import { fireVictoryPrompt } from "@/lib/viral/victoryPromptBus";
import { CreatorDiscoveryCta } from "@/components/creator/CreatorDiscoveryCta";
import { sponsorFor } from "@/lib/sponsor/sponsorMoments";
import { SponsorMomentBadge } from "@/components/sponsor/SponsorMomentBadge";
import { createChallenge, type ChallengeRecord } from "@/lib/viralSprintV3/challengeFriend";
import { sharePrediction, shareChallenge } from "@/lib/whatsappViral/whatsappEngine";

interface PredictionCardProps {
  visible: boolean;
  onClose?: () => void;
  /** Override question text — when not provided, the original default is used */
  question?: string;
  /** Unique key for this question instance (used for audit + de-dupe) */
  questionId?: string;
}

export function PredictionCard({ visible, onClose, question, questionId }: PredictionCardProps) {
  const [countdown, setCountdown] = useState(Math.ceil(PREDICTION_IDLE_MS / 1000));
  const [vote, setVote] = useState<"yes" | "no" | null>(null);
  const [stats] = useState({ yes: 68, no: 32 });
  const [history, setHistory] = useState<PredictionHistoryEntry[]>([]);
  const [wonThisVote, setWonThisVote] = useState(false);
  useEffect(() => { setHistory(getPredictionHistory()); }, []);
  const [closing, setClosing] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [interacted, setInteracted] = useState(false);
  // P0-DISCOVERY-02 — local Challenge Friend status. Backend-agnostic.
  const [challenge, setChallenge] = useState<ChallengeRecord | null>(null);
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  // Reuses the already-cached leaderboard query — no new polling/realtime.
  const lb = useQuery({
    queryKey: ["bracket", "lb"],
    queryFn: () => getBracketLeaderboard({ data: { limit: 200 } }),
    enabled: wonThisVote,
    staleTime: 60_000,
  });
  const ranking = useMemo(() => {
    const rows = lb.data?.rows ?? [];
    const total = rows.length;
    if (!user || total === 0) return { rank: null as number | null, total };
    const idx = rows.findIndex((r) => r.user_id === user.id);
    return { rank: idx >= 0 ? idx + 1 : null, total };
  }, [lb.data, user]);
  const shareLogro = (channel: "whatsapp" | "facebook" | "x") => {
    void trackEvent("prediction_share_logro_clicked", { channel, question_id: questionId ?? null });
    if (channel === "whatsapp") {
      // Route WhatsApp shares through the centralized engine so analytics
      // (wa_share_initiated / completed / failed / destination) stay consistent.
      void sharePrediction(
        {
          matchLabel: q,
          pickLabel: ranking.rank ? `🏆 #${ranking.rank}` : "🏆",
          predictionId: questionId ?? null,
        },
        { source: "prediction_card_logro" },
      );
      return;
    }
    const url = buildCanonicalShareUrl("/quinielas", { source: "prediction_card", medium: channel, campaign: "logro" });
    const rankPart = ranking.rank ? ` Estoy #${ranking.rank} en el ranking.` : "";
    const msg = `🏆 Acerté mi predicción en VerseSport: "${q}".${rankPart} ¡Juega tu quiniela!`;
    const enc = encodeURIComponent;
    const targets: Record<"facebook" | "x", string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      x: `https://twitter.com/intent/tweet?text=${enc(msg)}&url=${enc(url)}`,
    };
    if (typeof window !== "undefined") window.open(targets[channel], "_blank", "noopener,noreferrer");
  };
  const q = question ?? t("prediction_default_q");
  const sponsor = sponsorFor("prediction_explosion");
  // PR-35B — dedup gate: don't re-show a prediction the user already answered.
  const gated = !predictionCanDisplay({ pollType: "prediction", question: q });
  // Master attention — prediction owns the screen at prio 70 while visible,
  // and yields entirely to higher-priority surfaces (sponsor, viral, big moment,
  // global takeover). Never co-renders with an active poll.
  const yieldToHigher = useAttentionYield("prediction");
  useClaimAttention("prediction", visible && !gated && !yieldToHigher, 12_000);
  const swipeRef = useRef<{ x: number; y: number; active: boolean; dx: number }>({
    x: 0, y: 0, active: false, dx: 0,
  });
  const [swipeDx, setSwipeDx] = useState(0);

  useEffect(() => {
    if (!visible) {
      setCountdown(Math.ceil(PREDICTION_IDLE_MS / 1000));
      setVote(null);
      setClosing(false);
      setMinimized(false);
      setInteracted(false);
      return;
    }
    trackEvent("prediction_view", {
      question: q,
      question_id: questionId ?? null,
      surface: "in_radar_card",
    });
    const startedAt = Date.now();
    const maxMs = interacted ? PREDICTION_INTERACTED_MS : PREDICTION_IDLE_MS;
    let cancelled = false;
    const id = setInterval(() => {
      setCountdown((c) => {
        const next = Math.max(0, c - 1);
        if (next === 0 && !cancelled) {
          cancelled = true;
          clearInterval(id);
          // PR-40A.1 — consolidated lifecycle event (sampled 1:20).
          // Replaces redundant prediction_card_auto_closed + prediction_auto_minimized.
          void trackEvent("prediction_lifecycle", {
            phase: "minimized",
            question_id: questionId ?? null,
            answered: false,
            reason: "timeout",
            ms: Date.now() - startedAt,
          });
          setMinimized(true);
        }
        return next;
      });
    }, 1000);
    setCountdown(Math.ceil(maxMs / 1000));
    return () => { cancelled = true; clearInterval(id); };
  }, [visible, onClose, q, questionId, interacted]);

  // Fast auto-close after voting — duration honors the PR-35A lifecycle config.
  useEffect(() => {
    if (!visible || !vote) return;
    const startedAt = Date.now();
    const dismissMs = postVoteDismissMs();
    const fadeId = window.setTimeout(() => setClosing(true), Math.max(800, dismissMs - 300));
    const id = window.setTimeout(() => {
      // PR-40A.1 — consolidated post-vote dismissal.
      void trackEvent("prediction_lifecycle", {
        phase: "closed",
        question: q,
        question_id: questionId ?? null,
        answered: true,
        after_vote: true,
        ms: Date.now() - startedAt,
      });
      onClose?.();
    }, dismissMs);
    return () => { window.clearTimeout(id); window.clearTimeout(fadeId); };
  }, [vote, visible, onClose, q, questionId]);

  const onTouchStart = (e: React.TouchEvent) => {
    const t0 = e.touches[0];
    swipeRef.current = { x: t0.clientX, y: t0.clientY, active: true, dx: 0 };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!swipeRef.current.active) return;
    const dx = e.touches[0].clientX - swipeRef.current.x;
    swipeRef.current.dx = dx;
    setSwipeDx(dx);
  };
  const onTouchEnd = () => {
    const { dx } = swipeRef.current;
    swipeRef.current.active = false;
    if (Math.abs(dx) > 80) {
      trackEvent("prediction_dismissed", { question_id: questionId ?? null, source: "swipe" });
      trackEvent("mobile_overlay_auto_dismissed", { surface: "prediction_card", source: "swipe" });
      setClosing(true);
      window.setTimeout(() => onClose?.(), 220);
    } else {
      setSwipeDx(0);
    }
  };

  if (!visible) return null;
  if (gated) return null;
  if (yieldToHigher) return null;

  if (minimized) {
    return (
      <button
        onClick={() => {
          setMinimized(false);
          setInteracted(true);
          setCountdown(Math.ceil(PREDICTION_INTERACTED_MS / 1000));
          void trackEvent("prediction_reopened", { question_id: questionId ?? null });
        }}
        aria-label="Reabrir predicción"
        className="fixed left-1/2 z-30 -translate-x-1/2 flex items-center gap-1.5 rounded-full border border-[oklch(0.82_0.24_145)]/50 bg-background/90 px-3 text-[11px] font-bold backdrop-blur shadow-md hover:scale-105"
        style={{
          height: PREDICTION_BADGE_HEIGHT_PX,
          bottom: isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 80px)" : 24,
        }}
      >
        <Sparkles className="h-3.5 w-3.5 text-[oklch(0.82_0.24_145)]" />
        ✨ Predicción disponible
      </button>
    );
  }

  const cast = (choice: "yes" | "no") => {
    if (vote) return;
    setInteracted(true);
    setVote(choice);
    // PR-35B — register so this prediction doesn't re-appear this match.
    predictionRegisterAnswer({ pollType: "prediction", question: q });
    void bumpInsight("predictions_made");
    void awardPoints("prediction_vote", { choice });
    trackEvent("prediction_vote", {
      question: q,
      question_id: questionId ?? null,
      choice,
      country: "US",
      city: "Miami",
      source: "prediction_card",
    });
    trackEvent("prediction_answered", { question: q, question_id: questionId ?? null, choice });
    trackEvent("survey_response", {
      survey_id: questionId ?? "next_goal_2min",
      answer: choice,
    });
    // Resolve outcome against majority (demo ground truth) — fires celebration on hit.
    const correct = (choice === "yes" && stats.yes >= stats.no) ||
                    (choice === "no"  && stats.no  >  stats.yes);
    setWonThisVote(correct);
    const next = appendPredictionHistory({
      id: questionId ?? `pred_${Date.now()}`,
      label: q,
      correct,
    });
    setHistory(next);
    if (correct) {
      resolveOutcome({
        correct: true,
        surface: "prediction_card",
        questionId: questionId ?? undefined,
        pollType: "prediction",
      });
      // P0-DISCOVERY-01 — surface non-blocking Victory Card prompt.
      window.setTimeout(
        () =>
          fireVictoryPrompt({
            source: "prediction_correct",
            headline: `🏆 Acertaste: ${q}`,
          }),
        1200,
      );
    } else {
      toast.success(t("prediction_registered"), {
        description: choice === "yes" ? t("prediction_voted_yes") : t("prediction_voted_no"),
      });
    }
  };

  const launchChallenge = () => {
    void trackEvent("challenge_friend_clicked", {
      question_id: questionId ?? null,
      surface: "prediction_card",
    });
    const rec = createChallenge({ matchId: null, prediction: q });
    setChallenge(rec);
    void trackEvent("challenge_friend_status_changed", {
      id: rec.id,
      status: rec.status,
    });
    void shareChallenge(
      { code: rec.id, matchLabel: q },
      { source: "prediction_card_challenge" },
    ).then((r) => { if (!r.ok) openShareSheet(); })
     .catch(() => openShareSheet());
  };

  const challengeStatusLabel: Record<ChallengeRecord["status"], string> = {
    sent: "⏳ Pendiente",
    accepted: "✅ Aceptado",
    won: "🏆 Ganaste",
    lost: "💔 Perdiste",
  };

  // Mobile: render as compact bottom sheet ABOVE bottom nav, never covers radar.
  // Desktop: keep original top-centered card behavior.
  const wrapperClass = isMobile
    ? `pointer-events-auto fixed left-1/2 z-[45] -translate-x-1/2 px-3 w-full max-w-sm transition-all duration-300 ${closing ? "opacity-0 translate-y-2" : "opacity-100"}`
    : `pointer-events-auto absolute left-1/2 top-14 z-40 -translate-x-1/2 animate-slide-up px-3 w-full max-w-sm transition-opacity duration-300 ${closing ? "opacity-0" : "opacity-100"}`;
  const wrapperStyle = isMobile
    ? { bottom: "calc(env(safe-area-inset-bottom, 0px) + 68px)" }
    : undefined;

  return (
    <div
      className={wrapperClass}
      style={{ ...wrapperStyle, transform: swipeDx ? `translateX(${swipeDx}px)` : undefined }}
      onTouchStart={isMobile ? onTouchStart : undefined}
      onTouchMove={isMobile ? onTouchMove : undefined}
      onTouchEnd={isMobile ? onTouchEnd : undefined}
    >
      <div className={`relative overflow-hidden rounded-2xl border border-[oklch(0.82_0.24_145)]/40 bg-background/85 backdrop-blur-xl ${isMobile ? "p-2.5" : "p-4 animate-pulse-neon"}`}>
        <div className="absolute inset-0 opacity-20 pointer-events-none bg-gradient-to-br from-[oklch(0.82_0.24_145)] to-transparent" />
        <div className="relative">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className={`flex items-center justify-center rounded-lg bg-[oklch(0.82_0.24_145)] text-background ${isMobile ? "h-6 w-6" : "h-8 w-8"}`}>
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                {!isMobile && (
                  <div className="text-[10px] font-bold tracking-widest text-[oklch(0.82_0.24_145)]">
                    {t("prediction_badge")}
                  </div>
                )}
                <div className={`font-bold ${isMobile ? "text-[12px] leading-tight line-clamp-1" : "text-sm"}`}>{q}</div>
              </div>
            </div>
            <div className="flex h-7 min-w-[28px] items-center justify-center rounded-full bg-background/60 px-2 text-[11px] font-black tabular-nums">
              {countdown}s
            </div>
          </div>

          {!vote ? (
            <>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => cast("yes")}
                className="rounded-xl bg-[oklch(0.82_0.24_145)] px-4 py-2 text-xs font-bold text-background transition hover:scale-105 active:scale-95"
              >
                {t("yes")}
              </button>
              <button
                onClick={() => cast("no")}
                className="rounded-xl bg-secondary px-4 py-2 text-xs font-bold text-foreground transition hover:scale-105 active:scale-95"
              >
                {t("no")}
              </button>
            </div>
            <button
              type="button"
              onClick={() => {
                void trackEvent("challenge_friend_clicked", {
                  question_id: questionId ?? null,
                  surface: "prediction_card",
                });
                const rec = createChallenge({ matchId: null, prediction: q });
                void shareChallenge(
                  { code: rec.id, matchLabel: q },
                  { source: "prediction_card_challenge_pre_vote" },
                ).then((r) => { if (!r.ok) openShareSheet(); })
                 .catch(() => openShareSheet());
              }}
              className="mt-2 w-full rounded-xl border border-primary/60 bg-primary/15 px-3 py-2 text-xs font-black text-primary transition hover:bg-primary/25 active:scale-95"
            >
              ⚔️ ¿Quién sabe más de fútbol?
            </button>
            </>
          ) : (
            <div className="mt-3 space-y-1.5">
              {wonThisVote && (
                <div className="space-y-1.5 rounded-lg border border-[oklch(0.82_0.24_145)]/60 bg-[oklch(0.82_0.24_145)]/15 px-2.5 py-1.5 animate-pulse-neon">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-black tracking-wider text-[oklch(0.82_0.24_145)]">
                      🏆 ACERTASTE
                    </span>
                    <span className="text-[10px] font-bold text-[oklch(0.82_0.24_145)]">
                      🔥 +250 REP
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] leading-tight">
                      {ranking.rank ? (
                        <>
                          <span className="font-bold">Tu posición</span>{" "}
                          <span className="font-black tabular-nums text-[oklch(0.82_0.24_145)]">
                            #{ranking.rank}
                          </span>
                          {ranking.total > 0 && (
                            <span className="text-muted-foreground"> de {ranking.total}</span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted-foreground">
                          {lb.isLoading ? "Calculando ranking…" : "Sin ranking aún"}
                        </span>
                      )}
                    </div>
                    <Link
                      to="/quinielas"
                      onClick={() => void trackEvent("prediction_hub_view_ranking_clicked", {})}
                      className="rounded-md bg-[oklch(0.82_0.24_145)] px-2 py-0.5 text-[10px] font-black text-background hover:scale-105 transition"
                    >
                      Ver Ranking →
                    </Link>
                  </div>
                  <div className="flex items-center gap-1.5 pt-0.5">
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-muted-foreground">
                      <Share2 className="h-3 w-3" /> Compartir Logro
                    </span>
                    <button
                      type="button"
                      onClick={() => shareLogro("whatsapp")}
                      aria-label="Compartir en WhatsApp"
                      className="ml-auto rounded-md bg-background/70 px-1.5 py-0.5 text-[11px] font-bold hover:bg-background"
                    >
                      💬
                    </button>
                    <button
                      type="button"
                      onClick={() => shareLogro("facebook")}
                      aria-label="Compartir en Facebook"
                      className="rounded-md bg-background/70 px-1.5 py-0.5 text-[11px] font-bold hover:bg-background"
                    >
                      f
                    </button>
                    <button
                      type="button"
                      onClick={() => shareLogro("x")}
                      aria-label="Compartir en X"
                      className="rounded-md bg-background/70 px-1.5 py-0.5 text-[11px] font-bold hover:bg-background"
                    >
                      𝕏
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        void trackEvent("prediction_share_sheet_opened", {});
                        openShareSheet();
                      }}
                      aria-label="Más opciones para compartir"
                      className="rounded-md bg-primary/80 px-1.5 py-0.5 text-[10px] font-black text-primary-foreground hover:bg-primary"
                    >
                      +Recompensas
                    </button>
                  </div>
                  <div className="pt-1">
                    <VictoryCardCta source="prediction_correct" compact />
                  </div>
                  <button
                    type="button"
                    onClick={launchChallenge}
                    className="w-full rounded-md border border-primary/60 bg-primary/15 px-2 py-1 text-[10px] font-black text-primary hover:bg-primary/25"
                  >
                    ⚔️ ¿Quién sabe más de fútbol?
                  </button>
                  {challenge && (
                    <div className="rounded-md bg-background/70 px-2 py-1 text-[10px] font-bold text-foreground">
                      Reto a un amigo: {challengeStatusLabel[challenge.status]}
                    </div>
                  )}
                  <CreatorDiscoveryCta source="prediction_correct" />
                </div>
              )}
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <TrendingUp className="h-3 w-3 text-[oklch(0.82_0.24_145)]" />
                {t("community_result")}
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="w-6 font-bold">{t("yes")}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full bg-[oklch(0.82_0.24_145)]"
                    style={{ width: `${stats.yes}%` }}
                  />
                </div>
                <span className="w-8 text-right font-black tabular-nums">{stats.yes}%</span>
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="w-6 font-bold">{t("no")}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full bg-muted-foreground/60" style={{ width: `${stats.no}%` }} />
                </div>
                <span className="w-8 text-right font-black tabular-nums">{stats.no}%</span>
              </div>
              {history.length > 0 && (
                <div className="mt-2 border-t border-border/40 pt-1.5">
                  <div className="text-[9px] font-black uppercase tracking-wider text-muted-foreground mb-1">
                    Últimos aciertos
                  </div>
                  <ul className="space-y-0.5">
                    {history.slice(0, 3).map((h) => (
                      <li key={`${h.id}_${h.ts}`} className="flex items-center gap-1.5 text-[10px]">
                        <span className={h.correct ? "text-[oklch(0.82_0.24_145)]" : "text-muted-foreground/60"}>
                          {h.correct ? "✔" : "✖"}
                        </span>
                        <span className={`truncate ${h.correct ? "text-foreground" : "text-muted-foreground line-through"}`}>
                          {h.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {sponsor && (
            <div className="mt-1">
              <SponsorMomentBadge sponsor={sponsor} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}